import { SolidStatusPill, getSolidStatusBgColor } from '@odo/shared-ui';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useResourcesConfig } from '../config/ResourcesConfigContext';
import { useSearchScope } from '../context/CommandPaletteContext';
import { useResourcePageTitle } from '../hooks/useResourcePageTitle';
import { useResourcesLibrary } from '../providers/resourcesLibraryProvider';
import { CreateResourceButton, EditResourceButton } from '../authoring/AuthoringButtons';
import { type CategoryRailItem } from '../components/CategoryRail';
import InternalBadge from '../components/InternalBadge';
import PDFViewerModal from '../components/PDFViewerModal';
import PinButton from '../components/PinButton';
import RailListLayout from '../components/RailListLayout';
import ResourceKindIcon from '../components/ResourceKindIcon';
import ResourcesPageHeader from '../components/ResourcesPageHeader';
import { ResourceEmpty, ResourceError, ResourceLoading } from '../components/ResourceStates';
import type { HelpDocumentData } from '../providers/helpCenterProvider';
import { formatResourceDate } from '../utils/resourceIndex';

/**
 * Categories listed in the rail, on top of the "All" row. Ordered by document
 * count, so a newly created category sorts last - the cap has to stay well
 * clear of the number of categories in use or new ones silently never appear.
 */
const RAIL_CATEGORY_LIMIT = 50;

const HelpDocumentRow: React.FC<{
    doc: HelpDocumentData;
    basePath: string;
    onPreview: (fileId: string | null) => void;
}> = ({ doc, basePath, onPreview }) => (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 flex gap-4 items-start hover:border-gray-300 hover:shadow transition-all">
        <ResourceKindIcon kind="document" className="w-11 h-11" iconClassName="w-5 h-5" />
        <div className="flex-1 min-w-0 flex flex-col gap-2">
            <div className="flex items-center gap-2.5 flex-wrap">
                <Link
                    to={`${basePath}/help-center/${doc.slug}`}
                    className="text-base font-bold text-gray-800 hover:underline"
                >
                    {doc.title}
                </Link>
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-gray-100 text-gray-500">
                    PDF
                </span>
                <InternalBadge audience={doc.audience} />
                {doc.category.map((cat) => (
                    <SolidStatusPill key={cat} value={cat} compact />
                ))}
            </div>
            <span className="text-xs text-gray-400">
                Published {formatResourceDate(doc.createdAt)}
            </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
            <button
                type="button"
                onClick={() => onPreview(doc.pdfFileId)}
                className="h-8 inline-flex items-center justify-center px-3.5 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
                Preview
            </button>
            <EditResourceButton kind="helpDocument" item={doc} name={doc.title} />
            <PinButton kind="document" id={doc.id} title={doc.title} />
        </div>
    </div>
);

/** Help Center: full-height category rail beside an internally scrolling document list. */
export const HelpCenterPage: React.FC = () => {
    const { basePath } = useResourcesConfig();
    const { helpDocuments, isLoading, error, refresh } = useResourcesLibrary();
    const [category, setCategory] = useState<string | null>(null);
    const [previewFileId, setPreviewFileId] = useState<string | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    useResourcePageTitle('Help Center');
    useSearchScope('document');

    const sorted = useMemo(
        () => [...helpDocuments].sort((a, b) => a.title.localeCompare(b.title)),
        [helpDocuments]
    );

    const railItems = useMemo<CategoryRailItem[]>(() => {
        const counts = new Map<string, number>();
        for (const doc of sorted) {
            for (const cat of doc.category) counts.set(cat, (counts.get(cat) ?? 0) + 1);
        }
        return [
            {
                key: 'all',
                label: 'All',
                count: sorted.length,
                active: category === null,
                onSelect: () => setCategory(null)
            },
            ...Array.from(counts.entries())
                .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
                .slice(0, RAIL_CATEGORY_LIMIT)
                .map(([label, count]) => ({
                    key: label,
                    label,
                    count,
                    active: category === label,
                    onSelect: () => setCategory(label),
                    dotClassName: getSolidStatusBgColor(label)
                }))
        ];
    }, [sorted, category]);

    const visible = useMemo(
        () => (category ? sorted.filter((doc) => doc.category.includes(category)) : sorted),
        [sorted, category]
    );

    const body = () => {
        if (isLoading && sorted.length === 0)
            return <ResourceLoading label="Loading help documents…" />;
        if (error && sorted.length === 0) {
            return <ResourceError message={error} onRetry={() => refresh(true)} />;
        }
        if (sorted.length === 0) {
            return (
                <ResourceEmpty
                    title="No help documents yet"
                    description="There are no help documents available at the moment. Check back later."
                />
            );
        }

        return (
            <RailListLayout
                railItems={railItems}
                emptyState={
                    visible.length === 0 ? (
                        <ResourceEmpty
                            title="Nothing in this category"
                            description="Pick another category, or search everything with ⌘K."
                        />
                    ) : undefined
                }
            >
                {visible.map((doc) => (
                    <HelpDocumentRow
                        key={doc.id}
                        doc={doc}
                        basePath={basePath}
                        onPreview={(fileId) => {
                            setPreviewFileId(fileId);
                            setIsPreviewOpen(true);
                        }}
                    />
                ))}
            </RailListLayout>
        );
    };

    return (
        <>
            <div className="flex flex-col min-h-0 flex-1">
                <ResourcesPageHeader
                    title="Help Center"
                    searchPlaceholder="Search the Help Center…"
                    actions={<CreateResourceButton kind="helpDocument" />}
                />
                <div className="flex-1 min-h-0 px-8 pt-6 pb-8">{body()}</div>
            </div>

            {/* Rendered even when the document has no file: the viewer explains
                that rather than leaving the Preview button inert. */}
            {isPreviewOpen && (
                <PDFViewerModal fileId={previewFileId} onClose={() => setIsPreviewOpen(false)} />
            )}
        </>
    );
};

export default HelpCenterPage;
